import { useState } from "react";
import { PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useLoad } from "@/lib/hooks";
import { api, errMsg, fdatetime } from "@/lib/api";
import { PageHeader, Section, StatusBadge, EmptyRow, Field, NativeSelect, StackTable } from "@/components/common";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const STATUS_TONE = { active: "info", ended: "neutral", expired: "warning", revoked: "danger" };

export function SessionStatus({ s }) {
  return <StatusBadge tone={STATUS_TONE[s.status]} label={s.status} testId={`ops-session-status-${s.id}`} />;
}

export const remaining = (sec) => (sec > 0 ? `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m` : "—");

export default function OpsConsole() {
  const [tenants] = useLoad("/ops/tenants");
  const { user } = useAuth();
  const [rows] = useLoad("/ops/sessions");
  const [f, setF] = useState({ tenant_id: "", reason: "", hours: "2" });
  const [busy, setBusy] = useState(false);
  const valid = f.tenant_id && f.reason.trim().length >= 10;
  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ops/sessions", { ...f, hours: Number(f.hours) });
      localStorage.setItem("pressops_ops_token", localStorage.getItem("pressops_token") || "");
      localStorage.setItem("pressops_token", data.token);
      localStorage.removeItem("pressops_user");
      window.location.href = "/app";
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <>
      <PageHeader title="Ops console" subtitle="Start a tenant-scoped, time-bound support session. Every change you make is recorded with before and after values." />
      {user?.role === "support_l1" ? <div className="rounded-lg border px-4 py-3 text-sm po-tone-info" data-testid="ops-l1-note">Support Agents (L1) open Support Mode from a ticket in the Ticket queue. Sessions are view-only.</div> :
      <Section title="Start a session" testId="ops-start-card">
        <div className="grid gap-4 p-5 sm:grid-cols-2">
          <Field label="Tenant"><NativeSelect testId="ops-tenant-select" value={f.tenant_id} onChange={(v) => setF({ ...f, tenant_id: v })} placeholder="Select a tenant" options={(tenants || []).map((t) => ({ value: t.id, label: t.name }))} /></Field>
          <Field label="Session length"><NativeSelect testId="ops-hours-select" value={f.hours} onChange={(v) => setF({ ...f, hours: v })} options={[1, 2, 3, 4, 5, 6, 7, 8].map((h) => ({ value: String(h), label: `${h} hour${h > 1 ? "s" : ""}${h === 2 ? " (default)" : ""}` }))} /></Field>
          <div className="sm:col-span-2"><Field label="Reason for access (required, min 10 characters)">
            <Textarea data-testid="ops-reason-input" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="e.g. Ticket #482: customer cannot record a part payment" />
            <span className={`text-xs ${f.reason.trim().length >= 10 ? "text-slate-400" : "text-amber-700"}`} data-testid="ops-reason-count">{f.reason.trim().length}/10 characters minimum</span></Field></div>
          <div><Button data-testid="ops-start-session" disabled={!valid || busy} onClick={start} className="bg-[#13294B] hover:bg-[#1c3a66]"><PlayCircle className="mr-1 h-4 w-4" />{busy ? "Starting…" : "Start session"}</Button></div>
        </div>
      </Section>}
      <Section title="My sessions">
        <div className="overflow-x-auto"><StackTable data-testid="ops-my-sessions">
          <thead><tr><th>Tenant</th><th>Reason</th><th>Started</th><th className="po-num">Length</th><th>Status</th><th className="po-num">Remaining</th></tr></thead>
          <tbody>{rows?.length === 0 && <EmptyRow cols={6} text="No sessions yet." />}{rows?.map((s) => (
            <tr key={s.id}><td className="font-medium text-slate-900">{s.tenant_name}</td><td className="max-w-xs text-slate-600">{s.reason}</td><td>{fdatetime(s.started_at)}</td>
              <td className="po-num">{s.hours} h</td><td><SessionStatus s={s} />{s.break_glass && <StatusBadge tone="danger" label="break-glass" className="ml-1" />}</td><td className="po-num">{remaining(s.seconds_remaining)}</td></tr>))}</tbody>
        </StackTable></div>
      </Section>
    </>
  );
}
